import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, AlertTriangle, X, Loader2 } from "lucide-react";
import BottomSheet from "../../../../../components/dashboards/components/shared/BottomSheet";
import { useAddToChat } from "../../../../../components/chat/chatContext";

const confidenceStyles = {
  HIGH: "bg-emerald-500/20 text-emerald-200",
  MED: "bg-yellow-500/20 text-yellow-200",
  LOW: "bg-red-500/20 text-red-200"
};

const MobileNHLPredictionSheet = ({
  isOpen,
  game,
  prediction,
  selectionCount = 0,
  usageState = {},
  isLoading = false,
  onPredict,
  onClear,
  onClose,
  t
}) => {
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  const teams = game?.teams || {};
  const confidenceBadge = useMemo(() => {
    if (!prediction?.confidence) return null;
    const style = confidenceStyles[prediction.confidence] || "bg-white/10 text-white";
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-bold ${style}`}>
        {prediction.confidence}
      </span>
    );
  }, [prediction]);

  const handleAnalyzeInChat = async () => {
    if (!prediction || !game?.teams) return;
    const message = `${t?.("Analyze this NHL prediction:") || "Analyze this NHL prediction:"}

${teams?.away?.name} @ ${teams?.home?.name}

${t?.("Prediction:") || "Prediction:"} ${prediction.winner}${
      prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% ${t?.("confidence") || "confidence"})` : ""
    }
${t?.("Confidence Level:") || "Confidence Level:"} ${prediction.confidence}
${prediction.reason ? `${t?.("Reasoning:") || "Reasoning:"} ${prediction.reason}` : ""}

${t?.("Can you provide a deeper analysis of this matchup?") || "Can you provide a deeper analysis of this matchup?"}`;

    await handleAddToChat(message);
    navigate("/chat");
  };

  const renderTeam = (teamKey) => {
    const team = teams?.[teamKey];
    return (
      <div className="text-center">
        {team?.logo && (
          <img
            src={team.logo}
            alt={team.name}
            className="w-12 h-12 object-contain mx-auto mb-2"
          />
        )}
        <p className="text-sm font-semibold text-white">
          {team?.name || (teamKey === "home" ? "Home Team" : "Away Team")}
        </p>
      </div>
    );
  };

  const coinsPerGame = 7;
  const totalCoins = selectionCount * coinsPerGame;
  const canPredict = usageState.canUse && selectionCount > 0 && !isLoading && !prediction;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={t?.("Prediction") || "Prediction"}
      maxHeight="85vh"
    >
      {!game ? (
        <div className="p-6 text-center text-sm text-white/60">
          {t?.("Select a game to view its prediction.") || "Select a game to view its prediction."}
        </div>
      ) : (
        <div className="p-4 space-y-5">
          <div className="flex items-center justify-center gap-6">
            {renderTeam("away")}
            <div className="text-white/60 text-sm font-semibold">@</div>
            {renderTeam("home")}
          </div>

          <div className="bg-white/[0.05] rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-violet-500/15 text-violet-200">
                  <Sparkles size={16} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">
                    {prediction?.winner || t?.("Ready to predict?") || "Ready to predict?"}
                  </p>
                  {prediction?.confidenceNumeric && (
                    <p className="text-xs text-white/60">
                      {prediction.confidenceNumeric}% {t?.("confidence") || "confidence"}
                    </p>
                  )}
                </div>
              </div>
              {prediction && confidenceBadge}
            </div>

            {prediction ? (
              <>
                {prediction.reason && (
                  <p className="text-sm text-white/70 leading-relaxed">
                    {prediction.reason}
                  </p>
                )}

                <button
                  onClick={handleAnalyzeInChat}
                  className="w-full mt-2 px-4 py-2 rounded-md bg-violet-500/20 border border-violet-500/30 text-sm font-semibold text-violet-100 hover:bg-violet-500/30 transition-all"
                >
                  {t?.("Analyze in Chat →") || "Analyze in Chat →"}
                </button>

                {prediction.cached && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
                    <AlertTriangle size={16} className="text-amber-300 mt-0.5" />
                    <div className="text-xs text-amber-100">
                      <p>{t?.("Using cached predictions (1-hour cooldown active)") || "Using cached predictions (1-hour cooldown active)"}</p>
                      {prediction.cooldownRemaining && (
                        <p className="text-amber-200/80 mt-0.5">
                          {prediction.cooldownRemaining}m {t?.("remaining") || "remaining"}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-white/60">
                {t?.("Select up to 5 games and run Predict Winners.") || "Select up to 5 games and run Predict Winners."}
              </p>
            )}
          </div>

          {/* Selection summary + actions */}
          <div className="space-y-3">
            <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-xs text-white/50 uppercase tracking-wider">
                  {selectionCount} {selectionCount === 1 ? (t?.("Game Selected") || "Game Selected") : (t?.("Games Selected") || "Games Selected")}
                </p>
                <p className="text-xs text-white/60 font-mono">
                  {t?.("7 coins/game") || "7 coins/game"} • {totalCoins} {t?.("coins total") || "coins total"}
                </p>
              </div>
              {onClear && (
                <button
                  onClick={onClear}
                  className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-md transition"
                  aria-label={t?.("Clear selections") || "Clear selections"}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <button
              onClick={onPredict}
              disabled={!canPredict}
              className={`w-full flex items-center justify-center gap-2 px-6 py-2 font-medium text-sm transition-all backdrop-blur-sm rounded-md ${
                canPredict
                  ? "bg-violet-500/20 text-white border border-violet-500/30 hover:bg-violet-500/30 hover:border-violet-500/50"
                  : "bg-white/[0.05] text-white/30 cursor-not-allowed border border-white/[0.05]"
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>{t?.("Analyzing...") || "Analyzing..."}</span>
                </>
              ) : (
                <span>{t?.("Predict Winners") || "Predict Winners"}</span>
              )}
            </button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
};

export default MobileNHLPredictionSheet;
